import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { RootState, StoreDispatch } from '../../store/configure-store'
import { FormInputText } from '../forms/input/form-input-text'
import { Backlog } from '../util-cmps/backlog'
import { Spinner } from '../util-cmps/spinner'
import { setUserMsg } from '../../store/user'
import { newHttpService } from '../../services/new-http-service'
import { utilService } from '../../services/util-service'
import { getAuditControlsByCompanyNameQuery } from '../util-cmps/graphql-queries/audit-controls-query'
import { useEscapeKey } from '../../hooks/use-escape-key'
import { useScrollLock } from '../../hooks/use-scroll-lock'
import { useFocusTrap } from '../../hooks/use-focus-trap'
import { useDraggable } from '../../hooks/use-draggable'

interface RoleEntry {
    role_id: string
    audit_control_id?: number | null
}

interface AuditControlOption {
    id: number
    systemName: string
    objectiveName: string
    entityName: string
}

const AVAILABLE_ROLES = ['ac-read', 'ac-write', 'control-owner', 'overall-read', 'overall-write']

const requiresAuditControl = (roleId: string) => !roleId.startsWith('overall')

interface UserFormModalProps {
    companyName: string
    userToEdit?: {
        id?: string
        dbUserId?: string
        email: string
        firstName: string
        lastName: string
        dbRoles: string[]
        roleDetails: { role: string; audit_control_id: number; system_name: string; objective_name: string; entity_name: string }[]
    } | null
    existingEmails?: string[]
    onSuccess: () => void
    onClose: () => void
}

export const UserFormModal: React.FC<UserFormModalProps> = ({
    companyName, userToEdit, existingEmails = [], onSuccess, onClose
}) => {
    const isEditMode = !!userToEdit
    const dispatch = useDispatch<StoreDispatch>()
    const currentUser = useSelector((state: RootState) => state.users.user)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [lastModifiedSnippet, setLastModifiedSnippet] = useState<{ actor: string; time: string } | null>(null)

    const [email, setEmail] = useState(userToEdit?.email || '')
    const [firstName, setFirstName] = useState(userToEdit?.firstName || '')
    const [lastName, setLastName] = useState(userToEdit?.lastName || '')

    // Roles
    const [roleEntries, setRoleEntries] = useState<RoleEntry[]>(() => {
        if (userToEdit?.roleDetails?.length) {
            return userToEdit.roleDetails.map(rd => ({
                role_id: rd.role,
                audit_control_id: rd.audit_control_id || null,
            }))
        }
        if (userToEdit?.dbRoles?.length) {
            return userToEdit.dbRoles.map(r => ({ role_id: r, audit_control_id: null }))
        }
        return []
    })

    // Audit controls for control-owner assignment dropdown
    const [auditControls, setAuditControls] = useState<AuditControlOption[]>([])

    const [createInCognito, setCreateInCognito] = useState(false)
    const [cognitoPassword, setCognitoPassword] = useState('')
    const [cognitoCheckResult, setCognitoCheckResult] = useState<{ exists: boolean; in_group: boolean; status: string | null; login_url?: string } | null>(null)
    const [isCheckingCognito, setIsCheckingCognito] = useState(false)
    const [createdCredentials, setCreatedCredentials] = useState<{ email: string; password: string; loginUrl?: string } | null>(null)
    const copyBtnRef = useRef<HTMLButtonElement>(null)
    const generatePassword = () => {
        const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ'
        const lower = 'abcdefghjkmnpqrstuvwxyz'
        const digits = '23456789'
        const special = '!@#$%'
        const all = upper + lower + digits + special
        const required = [
            upper[Math.floor(Math.random() * upper.length)],
            lower[Math.floor(Math.random() * lower.length)],
            digits[Math.floor(Math.random() * digits.length)],
            special[Math.floor(Math.random() * special.length)],
        ]
        for (let i = required.length; i < 12; i++) required.push(all[Math.floor(Math.random() * all.length)])
        for (let i = required.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [required[i], required[j]] = [required[j], required[i]]
        }
        return required.join('')
    }
    const [suggestedPassword] = useState(generatePassword)

    const actorLabel = useCallback((email: string | null) => {
        if (!email) return 'Current admin'
        const local = email.split('@')[0]
        return local.split(/[._-]/).map((w: string) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
    }, [])

    const auditResourceId = userToEdit?.dbUserId || userToEdit?.id

    const loadLatestAuditSnippet = useCallback(async () => {
        if (!isEditMode || !auditResourceId) return
        try {
            const latest = await newHttpService.get('/api/backoffice/audit-log/latest', {
                company_name: companyName,
                resource_type: 'user',
                resource_id: auditResourceId,
            })
            if (latest?.created_at) {
                setLastModifiedSnippet({
                    actor: latest.actor_name || actorLabel(latest.actor_email),
                    time: utilService.formatDateTime(latest.created_at) || '—',
                })
            }
        } catch {
            // Keep the UI quiet if audit history is unavailable.
        }
    }, [actorLabel, auditResourceId, companyName, isEditMode])

    const checkCognitoRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    // Auto-check Cognito whenever email changes; in edit mode fires immediately on mount
    useEffect(() => {
        setCognitoCheckResult(null)
        if (checkCognitoRef.current) clearTimeout(checkCognitoRef.current)
        const target = email.trim()
        if (!target) return
        checkCognitoRef.current = setTimeout(async () => {
            setIsCheckingCognito(true)
            try {
                const res = await newHttpService.get('/api/backoffice/onboarding/check-cognito-user', { email: target, company_name: companyName })
                setCognitoCheckResult(res)
            } catch {
                setCognitoCheckResult(null)
            } finally {
                setIsCheckingCognito(false)
            }
        }, isEditMode ? 0 : 600)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [email])

    // Uncheck "manage in Cognito" if the user is already in the group
    useEffect(() => {
        if (cognitoCheckResult?.in_group) setCreateInCognito(false)
    }, [cognitoCheckResult])

    useEffect(() => {
        setLastModifiedSnippet(null)
        loadLatestAuditSnippet()
    }, [loadLatestAuditSnippet])

    // If credentials were created and user dismisses with Escape/X, still trigger data refresh
    const handleClose = useCallback(() => {
        if (createdCredentials) onSuccess()
        else onClose()
    }, [createdCredentials, onSuccess, onClose])

    useEscapeKey(handleClose)
    useScrollLock()
    const trapRef = useFocusTrap<HTMLDivElement>()
    const { modalRef, onHeaderMouseDown } = useDraggable<HTMLElement>()

    // Fetch audit controls for the dropdown (reuse existing GraphQL query)
    const fetchAuditControls = useCallback(async () => {
        try {
            const res = await newHttpService.graphql(companyName, getAuditControlsByCompanyNameQuery(companyName))
            const acData = res.auditControlsByCompanyName || []
            setAuditControls(acData.map((ac: any) => ({
                id: ac.id,
                systemName: ac.control?.mainSystem?.system?.name || '',
                objectiveName: ac.control?.objective?.name || '',
                entityName: ac.audit?.entity?.name || '',
            })))
        } catch { /* ignore */ }
    }, [companyName])

    useEffect(() => { fetchAuditControls() }, [fetchAuditControls])

    const acIdSet = new Set(auditControls.map(ac => ac.id))

    const addRoleEntry = () => {
        setRoleEntries(prev => [...prev, { role_id: '', audit_control_id: null }])
    }

    const updateRoleId = (index: number, roleId: string) => {
        setRoleEntries(prev => prev.map((r, i) => {
            if (i !== index) return r
            // Clear AC when switching to an overall role
            return { role_id: roleId, audit_control_id: requiresAuditControl(roleId) ? r.audit_control_id : null }
        }))
    }

    const updateRoleAcId = (index: number, acId: number | null) => {
        setRoleEntries(prev => prev.map((r, i) => i === index ? { ...r, audit_control_id: acId } : r))
    }

    const removeRoleEntry = (index: number) => {
        setRoleEntries(prev => prev.filter((_, i) => i !== index))
    }

    const validateRoles = (): string | null => {
        for (const entry of roleEntries) {
            if (!entry.role_id) return 'Each role entry must have a role selected'
            if (requiresAuditControl(entry.role_id)) {
                if (!entry.audit_control_id) {
                    return `Role "${entry.role_id}" requires an audit control ID`
                }
                if (!acIdSet.has(entry.audit_control_id)) {
                    return `Audit control ${entry.audit_control_id} does not belong to this company`
                }
            }
        }
        return null
    }

    const onSubmit = async () => {
        if (!email.trim()) {
            dispatch(setUserMsg({ msg: { type: 'danger', txt: 'Email is required' } }))
            return
        }
        if (!isEditMode && existingEmails.some(e => e.toLowerCase() === email.trim().toLowerCase())) {
            dispatch(setUserMsg({ msg: { type: 'danger', txt: `User with email "${email.trim()}" already exists` } }))
            return
        }
        const roleError = validateRoles()
        if (roleError) {
            dispatch(setUserMsg({ msg: { type: 'danger', txt: roleError } }))
            return
        }

        setIsSubmitting(true)
        try {
            const handleCognito = async (): Promise<{ msg: string; credentials: { email: string; password: string; loginUrl?: string } | null }> => {
                if (!createInCognito) return { msg: '', credentials: null }
                const userAlreadyExists = cognitoCheckResult?.exists === true
                const pw = userAlreadyExists ? '' : (cognitoPassword || suggestedPassword)
                try {
                    await newHttpService.post('/api/backoffice/onboarding/create-cognito-user', {
                        company_name: companyName,
                        email: email.trim(),
                        password: pw,
                    })
                    if (!userAlreadyExists) {
                        return {
                            msg: ' and created in Cognito',
                            credentials: { email: email.trim(), password: pw, loginUrl: cognitoCheckResult?.login_url },
                        }
                    }
                    return { msg: ' and added to Cognito group', credentials: null }
                } catch (cogErr: any) {
                    let errText = 'Cognito operation failed'
                    try { if (cogErr?.json) { const body = await cogErr.json(); errText = body.error || errText } } catch { /* ignore */ }
                    return { msg: ` (Cognito failed: ${errText})`, credentials: null }
                }
            }

            if (isEditMode) {
                await newHttpService.put('/api/backoffice/users/update', {
                    company_name: companyName,
                    email: email.trim(),
                    first_name: firstName.trim(),
                    last_name: lastName.trim(),
                    roles: roleEntries.filter(r => r.role_id),
                })
                setLastModifiedSnippet({
                    actor: actorLabel(currentUser),
                    time: utilService.formatDateTime(new Date().toISOString()) || '—',
                })
                const { msg: cognitoMsg, credentials } = await handleCognito()
                dispatch(setUserMsg({ msg: { type: 'success', txt: `User updated${cognitoMsg}` } }))
                if (credentials) { setCreatedCredentials(credentials); return }
            } else {
                await newHttpService.post('/api/backoffice/onboarding/setup-admin-users', {
                    company_name: companyName,
                    admin_users: [{ email: email.trim(), first_name: firstName.trim(), last_name: lastName.trim() }],
                })
                const hasNonDefaultRoles = roleEntries.some(r => r.role_id !== 'overall-write') ||
                    roleEntries.some(r => r.audit_control_id) ||
                    !roleEntries.some(r => r.role_id === 'overall-write')
                if (hasNonDefaultRoles && roleEntries.length > 0) {
                    await newHttpService.put('/api/backoffice/users/update', {
                        company_name: companyName,
                        email: email.trim(),
                        first_name: firstName.trim(),
                        last_name: lastName.trim(),
                        roles: roleEntries.filter(r => r.role_id),
                    })
                    setLastModifiedSnippet({
                        actor: actorLabel(currentUser),
                        time: utilService.formatDateTime(new Date().toISOString()) || '—',
                    })
                }
                const { msg: cognitoMsg, credentials } = await handleCognito()
                dispatch(setUserMsg({ msg: { type: 'success', txt: `User created in DB${cognitoMsg}` } }))
                if (credentials) { setCreatedCredentials(credentials); return }
            }

            onSuccess()
        } catch (err: any) {
            let errorMsg = isEditMode ? 'Failed to update user' : 'Failed to create user'
            try {
                if (err?.json) {
                    const body = await err.json()
                    errorMsg = body.error || errorMsg
                }
            } catch { /* ignore */ }
            dispatch(setUserMsg({ msg: { type: 'danger', txt: errorMsg } }))
        } finally {
            setIsSubmitting(false)
        }
    }

    const modalTitle = isEditMode ? 'Edit User' : 'Create User'

    return <div ref={trapRef}>
        <section ref={modalRef} className="user-form-modal" role="dialog" aria-modal="true" aria-label={modalTitle}>
            <header onMouseDown={onHeaderMouseDown}>
                <h2>{modalTitle}</h2>
                <button className="close-btn" onClick={handleClose} aria-label="Close">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                </button>
            </header>

            <section className="modal-form-body">
                <div className="form-section-card">
                    <div className="form-section-label">User Details</div>
                    <div className="form-row">
                        <div className="field">
                            <FormInputText keyName="email" label="Email" isRequired={true} value={email}
                                onChange={e => setEmail(e.target.value)} isDisabled={isEditMode} />
                        </div>
                    </div>
                    <div className="form-row">
                        <div className="field">
                            <FormInputText keyName="firstName" label="First Name" value={firstName}
                                onChange={e => setFirstName(e.target.value)} />
                        </div>
                        <div className="field">
                            <FormInputText keyName="lastName" label="Last Name" value={lastName}
                                onChange={e => setLastName(e.target.value)} />
                        </div>
                    </div>
                    {lastModifiedSnippet && (
                        <div className="audit-snippet">
                            <strong>Last modified by {lastModifiedSnippet.actor} at {lastModifiedSnippet.time}</strong>
                        </div>
                    )}
                </div>

                <div className="form-section-card">
                    <div className="form-section-label">Roles</div>
                    <div className="role-entries">
                        {roleEntries.map((entry, idx) => (
                            <div key={idx} className="role-entry-row">
                                <select
                                    className="role-select"
                                    value={entry.role_id}
                                    onChange={e => updateRoleId(idx, e.target.value)}
                                >
                                    <option value="">Select Role...</option>
                                    {AVAILABLE_ROLES.map(r => (
                                        <option key={r} value={r}>{r}</option>
                                    ))}
                                </select>
                                {requiresAuditControl(entry.role_id) && entry.role_id && (
                                    <select
                                        className="ac-select"
                                        value={entry.audit_control_id ?? ''}
                                        onChange={e => updateRoleAcId(idx, e.target.value ? Number(e.target.value) : null)}
                                    >
                                        <option value="">Select Audit Control...</option>
                                        {auditControls.map(ac => (
                                            <option key={ac.id} value={ac.id}>
                                                AC {ac.id} — {ac.systemName ? `${ac.systemName} / ` : ''}{ac.objectiveName} ({ac.entityName})
                                            </option>
                                        ))}
                                    </select>
                                )}
                                <button type="button" className="ac-remove-btn" onClick={() => removeRoleEntry(idx)}
                                    title="Remove">&times;</button>
                            </div>
                        ))}
                        <button type="button" className="ac-add-btn" onClick={addRoleEntry}>
                            + Add Role
                        </button>
                    </div>
                </div>

                <div className="form-section-card">
                    <div className="form-section-label">Cognito</div>
                    {email.trim() && !createdCredentials && (
                        <div style={{ marginBottom: '0.75rem', fontSize: '0.85rem' }}>
                            {isCheckingCognito && <span style={{ color: '#666' }}>Checking Cognito...</span>}
                            {!isCheckingCognito && cognitoCheckResult && (
                                <div style={{ padding: '0.5rem', background: '#f5f5f5', borderRadius: '4px' }}>
                                    {cognitoCheckResult.exists
                                        ? <>Status: <strong>{cognitoCheckResult.status}</strong>{cognitoCheckResult.in_group ? ` — already in ${companyName} group` : ` — not yet in ${companyName} group`}</>
                                        : 'Not found in Cognito — a new account will be created if you check the box below.'}
                                </div>
                            )}
                        </div>
                    )}
                    {!createdCredentials && cognitoCheckResult?.in_group !== true && (
                        <label className="role-checkbox">
                            <input
                                type="checkbox"
                                checked={createInCognito}
                                onChange={() => setCreateInCognito(!createInCognito)}
                            />
                            {cognitoCheckResult?.exists
                                ? `Add to ${companyName} Cognito group`
                                : 'Also create in AWS Cognito'}
                        </label>
                    )}
                    {createInCognito && cognitoCheckResult && !cognitoCheckResult.exists && !createdCredentials && (
                        <div className="form-row" style={{ marginTop: '0.75rem' }}>
                            <div className="field">
                                <FormInputText keyName="cognitoPassword" label="Password" value={cognitoPassword}
                                    onChange={e => setCognitoPassword(e.target.value)}
                                    placeholder={suggestedPassword} />
                                <span className="field-hint">Leave empty to use: {suggestedPassword}</span>
                            </div>
                        </div>
                    )}
                    {createdCredentials && (
                        <div className="created-user-card">
                            <div className="created-user-info">
                                <span className="created-user-email">{createdCredentials.email}</span>
                                <span className="created-user-password">Password: {createdCredentials.password}</span>
                                {createdCredentials.loginUrl && (
                                    <span className="created-user-password">Login: {createdCredentials.loginUrl}</span>
                                )}
                            </div>
                            <button
                                ref={copyBtnRef}
                                className="copy-credentials-btn"
                                onClick={() => {
                                    const text = `Hi,\n\nYour AudiTech portal account has been created.\n\nEmail: ${createdCredentials!.email}\nPassword: ${createdCredentials!.password}${createdCredentials!.loginUrl ? `\nLogin: ${createdCredentials!.loginUrl}` : ''}\n\nPlease log in and change your password.`
                                    navigator.clipboard.writeText(text)
                                    if (copyBtnRef.current) {
                                        copyBtnRef.current.textContent = 'Copied!'
                                        setTimeout(() => { if (copyBtnRef.current) copyBtnRef.current.textContent = 'Copy' }, 1500)
                                    }
                                }}
                            >Copy</button>
                        </div>
                    )}
                </div>

                <div className="form-actions">
                    {createdCredentials
                        ? <button className="main" onClick={onSuccess}>Done</button>
                        : <>
                            {!isSubmitting && <button className="main" onClick={onSubmit} disabled={isSubmitting || isCheckingCognito}>
                                {isEditMode ? 'Save' : 'Create'}
                            </button>}
                            {isSubmitting && <span className="spinner"><Spinner isSmall={true} /></span>}
                        </>
                    }
                </div>
            </section>
        </section>
        <Backlog zIndex={199} onCloseBacklog={handleClose} />
    </div>
}
